import { z } from 'zod';
import { conversationTypeScheme } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { avatarUrlSchema } from '@/utils/schemas';

const navigateToRouteScheme = z.enum([
  'my-qr-code',
  'my-collections',
  'privacy-settings',
  'messaging-settings',
  'friend-applications',
]);

// comm event got from outside
export const commEventSchema = z.discriminatedUnion('event', [
  z.object({ event: z.literal('initialize') }),
  z.object({ event: z.literal('binding-fail') }),
  z.object({ event: z.literal('sign-out') }),
  z.object({ event: z.literal('close') }),
  z.object({ event: z.literal('navigate-back') }),
  z.object({
    event: z.literal('open'),
    data: z
      .object({
        navigate: navigateToRouteScheme,
        dismissible: z.boolean(),
      })
      .partial()
      .optional(),
  }),
  z.object({
    event: z.literal('register'),
    data: z.object({ token: z.string() }),
  }),
  z.object({
    event: z.literal('binding-success'),
    data: z.object({ token: z.string() }),
  }),
  z.object({
    event: z.literal('change-locale'),
    data: z.object({ locale: z.string() }),
  }),
  z.object({
    event: z.literal('fcm-token'),
    data: z.object({ token: z.string() }),
  }),
  z.object({
    event: z.literal('session'),
    data: z.object({
      type: conversationTypeScheme,
      id: z.number(),
      message: z.string().optional(),
    }),
  }),
  z.object({
    event: z.literal('request-user-session'),
    data: z.object({
      id: z.number(),
    }),
  }),
  z.object({
    event: z.literal('group-info'),
    data: z.object({
      id: z.number(),
      remark: z.string().optional(),
    }),
  }),
  z.object({
    event: z.literal('header-action'),
    data: z.object({
      key: z.string(),
      label: z.string(),
      icon: avatarUrlSchema,
      style: z.record(z.string(), z.unknown()),
    }),
  }),
  z.object({
    event: z.literal('clear-header-action'),
    data: z.object({
      key: z.string(),
    }),
  }),
  z.object({ event: z.literal('change-notification-settings') }),
  z.object({ event: z.literal('check-version-update') }),
  z.object({ event: z.literal('trigger-version-update') }),
  z.object({ event: z.literal('join-group') }),
  z.object({
    event: z.literal('subscribe-session'),
    data: z.object({
      type: z.literal('new-message'),
      session: z
        .object({
          type: z.literal('user'),
          id: z.number(),
        })
        .optional(),
    }),
  }),
  z.object({
    event: z.literal('unsubscribe-session'),
    data: z.object({
      type: z.literal('new-message'),
      session: z
        .object({
          type: z.literal('user'),
          id: z.number(),
        })
        .optional(),
    }),
  }),
]);

export type CommEvent = z.infer<typeof commEventSchema>;

export type HeaderActionData = Extract<CommEvent, { event: 'header-action' }>['data'];

const REQUEST_USER_SESSION_REGEXP = /request-user-session-(\d+)/i;
const GROUP_INFO_REGEXP = /group-info-(\d+)-(.*)$/i;

export function getNavigateRoute(v: keyof typeof navigateToRouteScheme.Enum | string | undefined): string {
  if (v === undefined) return ROUTES.MESSAGES.$buildPath({ searchParams: { from: 'onboard' } });
  if (v.startsWith('/')) return v;
  if (v.startsWith('request-user-session-')) {
    const match = v.match(REQUEST_USER_SESSION_REGEXP);
    const id = Number.parseInt(match?.[1] ?? '0');
    return ROUTES.USER_BY_ID.$buildPath({
      params: { id },
      searchParams: { from: 'onboard' },
    });
  }
  if (v.startsWith('group-info-')) {
    const match = v.match(GROUP_INFO_REGEXP);
    const id = Number.parseInt(match?.[1] ?? '0');
    const remark = match?.[2] ?? '';
    return ROUTES.GROUP_BY_ID.$buildPath({
      params: { id },
      searchParams: { from: 'onboard', remark },
    });
  }

  switch (v) {
    case 'my-qr-code':
      return ROUTES.MY.QR_CODE.$buildPath({ searchParams: { from: 'onboard' } });
    case 'my-collections':
      return ROUTES.MY.COLLECTIONS.$buildPath({ searchParams: { from: 'onboard' } });
    case 'privacy-settings':
      return ROUTES.MY.SETTINGS.PRIVACY.$buildPath({ searchParams: { from: 'onboard' } });
    case 'messaging-settings':
      return ROUTES.MY.SETTINGS.MESSAGING.$buildPath({ searchParams: { from: 'onboard' } });
    case 'friend-applications':
      return ROUTES.CONTACT.USERS.APPLICATIONS.$buildPath({ searchParams: { from: 'onboard' } });
    default:
      return ROUTES.MESSAGES.$buildPath({ searchParams: { from: 'onboard' } });
  }
}
